import React, { useMemo, useRef } from 'react';
import { Field, FieldUpdatePayload } from '..';
import FluentForm from 'components/v2/Form/FluentForm';
import Form from 'components/v2/Form/Form';
import { useQuery } from '@tanstack/react-query';
import { Button, ButtonGroup } from '../../../components/v2/Button/Button';
import { FieldType } from '../../../components/v2/Form/types';
import { useTranslation } from 'utils/translation';
import Info from '../../../components/v2/Info/Info';
import { Dir } from '../../dirs';
import { patchUpdateDir, readDir } from '../../dirs/service/api';
import { mapDirectoryTranslatedTitle } from '../../dirs/utils';
import ConditionalFieldCheckerModern from '../components/ConditionalFieldCheckerModern';
import {
  fetchIntelligentFields,
  updateIntelligentFields,
} from '../service/api';
import Loader from 'components/v2/Loader';
import Section from 'components/v2/Section/Section';
import Spacer from 'components/v2/Spacer/Spacer';
import { groupBy } from 'lodash';
import { useAppSettings } from '../../ui/hooks';
import Tabs from '../../../components/v2/Tabs/Tabs';
import Typography from '../../../components/v2/Typography/Typography';
import {
  mapPayloadToValues,
  mapValuesToPayload,
  pickPersistableFieldValues,
} from '../utils';
import { useModalContent } from 'components/v2/Modal/useModalContent';
import { useAppFeatures } from '../../ui/selectors/app';
import { File } from '../../files';
import {
  useFileIntelligenceFields,
  useIntelligenceLayerFieldProps,
  useUpdateFileIntelligenceFields,
} from '../hooks';
import QueryResult from '../../../components/v2/Query/QueryResult';
import FieldProviders from '../components/FieldProviders';
import { applyFilters, Filters } from '../../../core';
import { useDebounceCallback } from 'usehooks-ts';
import http from '../../../utils/http';
import Skeleton from '@mui/material/Skeleton';
import repeat from '../../../utils/repeat';

export type Props = {
  identifier: string;
  enableTitle?: boolean;
  /** Form heading shown above fields (e.g. bulk update). */
  formTitle?: string | React.ReactElement;
  /** When set, submit sends only fields whose current values are persistable (non-empty for bulk partial update). */
  onlySubmitPersistableValues?: boolean;
  resolver(): Promise<[any, Field[]]>;
  persister(values: FieldUpdatePayload & { title?: any }): Promise<void>;
  onSuccess?(): void;
  /** FluentForm passes (error, payload); bulk flow may omit args when forwarding. */
  onError?(error?: unknown, payload?: unknown): void;
  renderButtons?: boolean;
  formId?: string;
};

const SingleField = ({ field }: { field: Field }) => {
  const { fieldProps, values } = useIntelligenceLayerFieldProps(field);

  const FieldComponent = useMemo(
    () =>
      applyFilters(Filters.intelligenceLayer.fieldComponentModern, Form.Field, {
        props: fieldProps,
      }),
    [fieldProps.name, fieldProps.type],
  );

  return (
    <FieldProviders field={field}>
      <ConditionalFieldCheckerModern
        condition={field.settings?.condition}
        values={values}
      >
        <FieldComponent {...fieldProps} />
      </ConditionalFieldCheckerModern>
    </FieldProviders>
  );
};

const SingleFieldRow = ({ item }: { item: Field }) => {
  return (
    <React.Fragment key={item.id}>
      {item.settings?.title && (
        <Typography component="h2" variant="title" uppercase>
          {item.settings.title}
        </Typography>
      )}
      {item.settings?.subTitle && (
        <>
          <Spacer size={2} />
          <Typography component="strong" variant="subtitle">
            {item.settings.subTitle}
          </Typography>
        </>
      )}
      <SingleField field={item} />
    </React.Fragment>
  );
};

export const BasicFieldsForm = ({
  fields,
  persister,
  onSuccess,
  onError,
  defaultValues,
  enableTitle = false,
  enableSuggestedValues = false,
  formTitle,
  onlySubmitPersistableValues = false,
  renderButtons = true,
  formId,
  ...rest
}: {
  fields: Field[];
  enableSuggestedValues?: boolean;
} & Pick<
  Props,
  | 'renderButtons'
  | 'persister'
  | 'onSuccess'
  | 'onError'
  | 'enableTitle'
  | 'formTitle'
  | 'onlySubmitPersistableValues'
  | 'formId'
> &
  Pick<
    React.ComponentProps<typeof Form>,
    'defaultValues' | 'onChange' | 'errors'
  >) => {
  const {
    settings: { locales },
  } = useAppSettings();
  const { t } = useTranslation();
  const groups = groupBy(fields, (item) => item.group?.name.toLowerCase());
  const tabs = Object.keys(groups);
  const localeTabs = locales.filter((item: string) => tabs.includes(item));
  const formRef = useRef<any>();

  const fillSuggestedValues = useDebounceCallback(async (values) => {
    const suggested = await http.post<never, Record<string, any>>(
      '/api/device/suggested-values',
      mapValuesToPayload(values),
    );

    Object.keys(suggested).forEach((field) => {
      if (formRef.current?.formState?.touchedFields[field]) {
        return;
      }

      formRef.current?.setValue(field, suggested[field], {
        shouldTouch: false,
      });
    });
  }, 500);

  const onChange = (values: any) => {
    if (enableSuggestedValues) {
      fillSuggestedValues(values);
    }

    rest.onChange?.(values);
  };

  if (fields.length === 0) {
    return (
      <Section titleText={formTitle}>
        <Info type="info" message={t('no_data_found')} />
        <Spacer size={2} />
        <ButtonGroup align="right">
          <Button onClick={onSuccess}>{t('Close')}</Button>
        </ButtonGroup>
      </Section>
    );
  }

  return (
    <Section padding="small" titleText={formTitle}>
      <FluentForm
        {...rest}
        formId={formId}
        renderButtons={renderButtons}
        formRef={formRef}
        onChange={onChange}
        action={async (values) => {
          if (onlySubmitPersistableValues) {
            return persister(
              pickPersistableFieldValues(
                values as Record<string, any>,
              ) as FieldUpdatePayload,
            );
          }
          return persister(values as FieldUpdatePayload & { title?: any });
        }}
        onSuccess={onSuccess}
        onError={onError}
        defaultValues={{
          ...defaultValues,
          ...mapPayloadToValues(fields),
        }}
      >
        {tabs.length > 1 ? (
          <Tabs fullWidth>
            <Tabs.List>
              {localeTabs.map((locale: string) => (
                <Tabs.Tab key={locale}>{locale}</Tabs.Tab>
              ))}
            </Tabs.List>
            {localeTabs.map((locale: string, index: number) => (
              <Tabs.Panel key={locale}>
                {enableTitle && (
                  <Form.Field
                    rules={{ required: true }}
                    name={`title.${locale}`}
                    type={FieldType.Text}
                    label={t('Title')}
                    autoFocus={index === 0}
                  />
                )}
                {groups[locale]!.map((item) => (
                  <SingleFieldRow item={item} key={item.id} />
                ))}
              </Tabs.Panel>
            ))}
          </Tabs>
        ) : (
          <>
            {enableTitle && (
              <Form.Field
                rules={{ required: true }}
                name="title"
                type={FieldType.Text}
                label={t('Title')}
                autoFocus
              />
            )}
            {fields.map((item) => (
              <SingleFieldRow item={item} key={item.id} />
            ))}
          </>
        )}
      </FluentForm>
    </Section>
  );
};

export const IntelligenceFieldsForm = ({
  identifier,
  resolver,
  persister,
  onSuccess,
  onError,
  enableTitle = false,
  formTitle,
  onlySubmitPersistableValues,
  renderButtons,
  formId,
}: Props) => {
  const { translatableDirectories } = useAppFeatures();
  const { data = [], isLoading } = useQuery({
    queryFn: resolver,
    queryKey: [identifier],
    gcTime: 0,
    retry: false,
  });

  if (isLoading) {
    return (
      <Section titleText={formTitle}>
        {repeat(7, (i) => (
          <React.Fragment key={i}>
            <Skeleton height={16} width="200px" />
            <Skeleton height={64} />
            <Spacer size={2} />
          </React.Fragment>
        ))}
      </Section>
    );
  }

  const [title = translatableDirectories ? {} : '', fields = []] = data;

  return (
    <BasicFieldsForm
      enableTitle={enableTitle}
      formTitle={formTitle}
      onlySubmitPersistableValues={onlySubmitPersistableValues}
      defaultValues={{
        title,
      }}
      fields={fields}
      persister={persister}
      onSuccess={onSuccess}
      onError={onError}
      renderButtons={renderButtons}
      formId={formId}
    />
  );
};

export const DirectoryIntelligenceFieldsForm = ({
  id,
  onSuccess,
  enableTitle = false,
  groups,
  formId,
}: Pick<Dir, 'id'> &
  Pick<Props, 'onSuccess' | 'enableTitle' | 'formId'> & {
    groups?: string[];
  }) => {
  const {
    settings: { locales },
  } = useAppSettings();
  const modal = useModalContent();
  const { translatableDirectories } = useAppFeatures();

  const onError = () => {
    modal.scrollToTop();
  };

  const identifier = groups?.length
    ? `intelligence_values_of_directory_${id}_${[...groups].sort().join(',')}`
    : `intelligence_values_of_directory_${id}`;

  return (
    <IntelligenceFieldsForm
      enableTitle={enableTitle}
      identifier={identifier}
      resolver={() =>
        Promise.all([
          readDir({ id }).then((dir) =>
            translatableDirectories
              ? mapDirectoryTranslatedTitle(dir, locales)
              : dir.title,
          ),
          fetchIntelligentFields({ id, groups }),
        ])
      }
      persister={async ({ title, ...values }) => {
        if (enableTitle) {
          await patchUpdateDir({ id, title });
        }

        await updateIntelligentFields({
          dirId: id,
          fields: mapValuesToPayload(values),
          groups,
        });
      }}
      onSuccess={onSuccess}
      onError={onError}
      renderButtons={formId ? false : undefined}
      formId={formId}
    />
  );
};

export const FileIntelligenceFieldsForm = ({
  id,
  onSuccess,
  fields = [],
  formId,
}: Pick<File, 'id'> &
  Pick<Props, 'onSuccess' | 'formId'> & {
    fields?: string[];
  }) => {
  const modal = useModalContent();
  const query = useFileIntelligenceFields({ id });
  const mutation = useUpdateFileIntelligenceFields({ id }, fields.length > 0);

  const onError = () => {
    modal.scrollToTop();
  };

  function filterByFields(field: Field) {
    if (!fields.length) {
      return true;
    }

    return fields.includes(field.name);
  }

  if (query.isLoading) {
    return (
      <Section>
        <Loader />
      </Section>
    );
  }

  return (
    <QueryResult
      {...query}
      render={(data) => (
        <BasicFieldsForm
          fields={data.filter(filterByFields)}
          persister={(values) => mutation.mutateAsync(values)}
          onSuccess={onSuccess}
          onError={onError}
          renderButtons={formId ? false : undefined}
          formId={formId}
        />
      )}
    />
  );
};
